<template>
  <div class="agent-card">
    <div class="agent-card__photo-wrapper">
      <img
        :src="data.image_mini"
        alt=""
        class="agent-card__photo"
      >
    </div>

    <div class="agent-card__content">
      <router-link
        :to="`/v/${data.alias}`"
        class="agent-card__name"
      >
        {{ data.name }}
      </router-link>

      <p class="agent-card__position">
        {{ data.role }}
      </p>

      <div
        v-if="data.review_count"
        class="agent-card__reviews"
      >
        <app-svg id="reviews" />
        <span>Отзывы: {{ data.review_count }}</span>
      </div>

      <div class="agent-card__socials">
        <a
          v-if="data.telegram"
          :href="data.telegram"
          class="agent-card__social"
        >
          <app-svg id="telegram" />
        </a>

        <a
          v-if="data.whatsapp"
          :href="data.whatsapp"
          class="agent-card__social"
        >
          <app-svg id="whatsapp" />
        </a>

        <a
          v-if="data.vk"
          :href="data.vk"
          class="agent-card__social agent-card__social_vk"
        >
          <app-svg id="vk" />
        </a>
      </div>

      <a
        :href="emailLink"
        class="agent-card__email"
      >{{ data.email }}</a>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

import AppSvg from '@/components/ui/AppSvg.vue';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
});

const emailLink = computed(() => {
  return `mailto:${props.data.email}`;
});
</script>

<style scoped lang="scss">
.agent-card {
  display: grid;
  grid-template-columns: 188px 1fr;
  grid-template-rows: auto;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;

  @include phone {
    grid-template-columns: 120px 1fr;
    align-items: center;
    padding: 8px;
    border: 1px solid #dcdfe1;
  }

  &__photo-wrapper {
    position: relative;
    width: 100%;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
    overflow: hidden;

    &:after {
      content: '';
      display: block;
      padding-bottom: 100%;
    }

    @include phone {
      max-height: 188px;
      max-width: 188px;
      aspect-ratio: 1;
      border-radius: 4px;
      overflow: hidden;
    }
  }

  &__photo {
    position: absolute;
    width: 100%;
  }

  &__content {
    display: flex;
    flex-direction: column;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
    border: 1px solid $color_grey-15;
    padding: 20px;

    @include phone {
      border: 0;
      padding: 0 12px;
    }
  }

  &__name {
    font-size: 20px;
    line-height: 28px;
    margin-bottom: 4px;
    font-weight: 700;

    @include phone {
      font-size: 16px;
      line-height: 18px;
    }
  }

  &__position {
    font-size: 15px;
    line-height: 20px;
    letter-spacing: .002em;
    color: $color_dark-50;
    font-weight: 500;
    margin-bottom: 6px;

    @include phone {
      font-size: 12px;
    }
  }

  &__reviews {
    display: flex;
    align-items: center;
    cursor: pointer;
    margin-bottom: 8px;

    > svg {
      width: 17px;
      height: 17px;
      margin-right: 4px;
    }

    > span {
      font-size: 12px;
      font-weight: 700;
      line-height: 20px;
      letter-spacing: 0.015em;
      color: $color_orange-50;
    }
  }

  &__socials {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }

  &__social {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    border-radius: 2px;

    svg {
      max-width: 15px;
      max-height: 15px;
    }

    &_vk {
      svg {
        max-width: 20px;
        max-height: 20px;
      }
    }
  }

  &__email {
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: .9px;
    text-transform: uppercase;
    position: relative;
    margin-top: auto;
    width: fit-content;

    &:hover {
      color: $color_orange-50;
    }

    &:after {
      position: absolute;
      top: 6px;
      right: -14px;
      width: 8px;
      height: 8px;
      content: '';
      border-top: 2px solid $color_orange-50;
      border-right: 2px solid $color_orange-50;
      transform: rotate(45deg);
    }

    @include phone {
      font-size: 12px;

      &:after {
        right: -8px;
      }
    }
  }
}
</style>
